import { useEffect, useReducer, useRef } from 'react';

import { loadingDataReducer, LoadingDataReducerAction, LoadingDataReducerState } from '../reducers/loadingData';

interface UseLoadingDataParams<T, E = unknown> {
    loader: () => Promise<T>;
    onError?: (e: E) => void;
}

export function useLoadingData<T, E = unknown>({ loader, onError }: UseLoadingDataParams<T, E>) {
    const [loadingMeta, dispatch] = useReducer(
        // for proper typing
        (prevState: LoadingDataReducerState<T>, action: LoadingDataReducerAction<T>) => {
            return loadingDataReducer<T>(prevState, action);
        },
        {
            data: undefined,
            state: 'loading',
        },
    );

    const loaderRef = useRef(loader);

    useEffect(() => {
        loaderRef.current = loader;
    }, [loader]);

    const loadData = async () => {
        if (loadingMeta.state !== 'loading') {
            return;
        }

        try {
            dispatch({
                type: 'ok',
                payload: await loaderRef.current(),
            });
        } catch (e) {
            console.error(e);
            onError && onError(e as E);
            dispatch({ type: 'error' });
        }
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => void loadData(), [loadingMeta.state]);

    return {
        loadingMeta,
        reload: () => dispatch({ type: 'loading' }),
        clear: () => dispatch({ type: 'init' }),
    };
}
